// src/components/books/BookSettingsModal.jsx - Complete implementation
import React, { useState, useEffect } from 'react';
import { 
  X, Save, Upload, Image as ImageIcon, Palette, Type, Globe, Eye, Settings,
  FileText, ClipboardList, MessageSquare, FormInput, Book, GraduationCap,
  CheckSquare, BarChart3, Users, Loader2, AlertCircle, Check, Trash2,
  Smartphone, Monitor, Tablet, Shield, Database, Mail, Lock, Clock,
  Timer, UserCheck, Plus
} from 'lucide-react';
import { storage } from '../../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { firestoreService } from '../../services/firestoreService';

const BOOK_CATEGORIES = [
  { value: 'notebook', label: 'Notebook', icon: Book, description: 'Personal notes, learning materials, and documentation' },
  { value: 'assignment', label: 'Assignment', icon: FileText, description: 'Homework, projects, and coursework submissions' },
  { value: 'test', label: 'Test', icon: GraduationCap, description: 'Exams, quizzes, and assessments with grading' },
  { value: 'investigation', label: 'Investigation', icon: BarChart3, description: 'Research projects and data analysis' },
  { value: 'project', label: 'Project', icon: CheckSquare, description: 'Multi-step projects with deliverables' },
  { value: 'quiz', label: 'Quiz', icon: ClipboardList, description: 'Short assessments and knowledge checks' },
  { value: 'survey', label: 'Survey', icon: MessageSquare, description: 'Data collection and feedback gathering' },
  { value: 'feedback', label: 'Feedback', icon: Users, description: 'Response collection and evaluation forms' },
  { value: 'form', label: 'Form', icon: FormInput, description: 'Data entry and information collection' }
];

const DEVICE_BREAKPOINTS = {
  mobile: { width: '375px', label: 'Mobile' },
  tablet: { width: '768px', label: 'Tablet' }, 
  desktop: { width: '1024px', label: 'Desktop' },
  wide: { width: '1440px', label: 'Wide' }
};

export const BookSettingsModal = ({ book, isOpen, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    // Basic Info
    botName: '',
    botCategory: 'notebook',
    description: '',
    
    // Visual Assets
    logoUrl: '',
    coverImageUrl: '',
    faviconUrl: '',
    ogImageUrl: '',
    
    // Styling
    primaryColor: '#2563eb',
    backgroundColor: '#ffffff',
    headerColor: '#2563eb',
    headerTextColor: '#ffffff',
    accentColor: '#f59e0b',
    borderColor: '#e5e7eb',
    fontFamily: 'Inter, system-ui, sans-serif',
    borderRadius: '8px',
    
    // Access & Privacy
    accessLevel: 'private',
    published: false,
    allowAnonymous: false,
    authorizedUsers: [],
    
    // Response Settings
    responseSettings: {
      timeLimit: null,
      allowResumeIncomplete: true,
      allowEditAfterSubmission: false,
      autoSaveInterval: 30,
      requireSubmission: true,
      maxAttempts: null,
      showTimer: true,
      warningTimes: [300, 60],
      enableProgressTracking: true,
      allowDownloadResponses: false,
      notifyOnSubmission: false,
      customSubmissionMessage: ''
    },
    
    // Domain & SEO
    customDomain: '',
    customPath: '',
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
    
    // Custom CSS
    customCSS: ''
  });

  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const [previewDevice, setPreviewDevice] = useState('desktop');
  
  // File states
  const [logoFile, setLogoFile] = useState(null);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [ogImageFile, setOgImageFile] = useState(null);
  
  // Preview states
  const [logoPreview, setLogoPreview] = useState('');
  const [coverImagePreview, setCoverImagePreview] = useState('');
  const [faviconPreview, setFaviconPreview] = useState('');
  const [ogImagePreview, setOgImagePreview] = useState('');

  const [newUserEmail, setNewUserEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'styling', label: 'Styling', icon: Palette },
    { id: 'assets', label: 'Media Assets', icon: ImageIcon },
    { id: 'access', label: 'Access & Privacy', icon: Shield },
    { id: 'responses', label: 'Response Settings', icon: Database },
    { id: 'notifications', label: 'Notifications', icon: Mail },
    { id: 'domain', label: 'Domain & SEO', icon: Globe },
    { id: 'css', label: 'Custom CSS', icon: Type },
    { id: 'preview', label: 'Preview', icon: Eye }
  ];

  useEffect(() => {
    if (book) {
      setFormData({
        botName: book.botName || '',
        botCategory: book.botCategory || 'notebook',
        description: book.description || '',
        logoUrl: book.logoUrl || '',
        coverImageUrl: book.coverImageUrl || '',
        faviconUrl: book.faviconUrl || '',
        ogImageUrl: book.ogImageUrl || '',
        primaryColor: book.primaryColor || '#2563eb',
        backgroundColor: book.backgroundColor || '#ffffff',
        headerColor: book.headerColor || '#2563eb',
        headerTextColor: book.headerTextColor || '#ffffff',
        accentColor: book.accentColor || '#f59e0b',
        borderColor: book.borderColor || '#e5e7eb',
        fontFamily: book.fontFamily || 'Inter, system-ui, sans-serif',
        borderRadius: book.borderRadius || '8px',
        accessLevel: book.accessLevel || 'private',
        published: book.published || false,
        allowAnonymous: book.allowAnonymous || false,
        authorizedUsers: book.authorizedUsers || [],
        responseSettings: {
          timeLimit: book.responseSettings?.timeLimit || null,
          allowResumeIncomplete: book.responseSettings?.allowResumeIncomplete !== false,
          allowEditAfterSubmission: book.responseSettings?.allowEditAfterSubmission || false,
          autoSaveInterval: book.responseSettings?.autoSaveInterval || 30,
          requireSubmission: book.responseSettings?.requireSubmission !== false,
          maxAttempts: book.responseSettings?.maxAttempts || null,
          showTimer: book.responseSettings?.showTimer !== false,
          warningTimes: book.responseSettings?.warningTimes || [300, 60],
          enableProgressTracking: book.responseSettings?.enableProgressTracking !== false,
          allowDownloadResponses: book.responseSettings?.allowDownloadResponses || false,
          notifyOnSubmission: book.responseSettings?.notifyOnSubmission || false,
          customSubmissionMessage: book.responseSettings?.customSubmissionMessage || ''
        },
        customDomain: book.customDomain || '',
        customPath: book.customPath || '',
        seoTitle: book.seoTitle || '',
        seoDescription: book.seoDescription || '',
        seoKeywords: book.seoKeywords || '',
        customCSS: book.customCSS || ''
      });

      // Set previews
      setLogoPreview(book.logoUrl || '');
      setCoverImagePreview(book.coverImageUrl || '');
      setFaviconPreview(book.faviconUrl || '');
      setOgImagePreview(book.ogImageUrl || '');
    }
  }, [book]);

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    if (field.startsWith('responseSettings.')) {
      const settingKey = field.replace('responseSettings.', '');
      setFormData(prev => ({
        ...prev,
        responseSettings: {
          ...prev.responseSettings,
          [settingKey]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleFileSelect = (fileType, event) => {
    const file = event.target.files[0];
    if (!file || !file.type.startsWith('image/')) return;

    const fileSetter = {
      logo: setLogoFile,
      cover: setCoverImageFile,
      favicon: setFaviconFile,
      ogImage: setOgImageFile
    }[fileType];

    const previewSetter = {
      logo: setLogoPreview,
      cover: setCoverImagePreview,
      favicon: setFaviconPreview,
      ogImage: setOgImagePreview
    }[fileType];

    if (fileSetter && previewSetter) {
      fileSetter(file);
      const reader = new FileReader();
      reader.onload = (e) => previewSetter(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadFile = async (file, fileType) => {
    if (!file) return null;
    
    try {
      setUploading(prev => ({ ...prev, [fileType]: true }));
      
      const timestamp = Date.now();
      const cleanFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `pb-${book?.id || 'temp'}/${fileType}/${timestamp}_${cleanFileName}`;
      
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (error) {
      console.error(`Error uploading ${fileType}:`, error);
      throw error;
    } finally {
      setUploading(prev => ({ ...prev, [fileType]: false }));
    }
  };

  const addAuthorizedUser = () => {
    setEmailError('');
    
    if (!newUserEmail.trim()) {
      setEmailError('Please enter an email address');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUserEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    if (formData.authorizedUsers.includes(newUserEmail)) {
      setEmailError('This email is already authorized');
      return;
    }

    setFormData(prev => ({
      ...prev,
      authorizedUsers: [...prev.authorizedUsers, newUserEmail.trim()]
    }));
    setNewUserEmail('');
  };

  const removeAuthorizedUser = (email) => {
    setFormData(prev => ({
      ...prev,
      authorizedUsers: prev.authorizedUsers.filter(user => user !== email)
    }));
  };

  const formatTime = (minutes) => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      // Upload all files
      const uploads = [];
      const uploadedUrls = {};

      if (logoFile) uploads.push({ file: logoFile, type: 'logo', field: 'logoUrl' });
      if (coverImageFile) uploads.push({ file: coverImageFile, type: 'cover', field: 'coverImageUrl' });
      if (faviconFile) uploads.push({ file: faviconFile, type: 'favicon', field: 'faviconUrl' });
      if (ogImageFile) uploads.push({ file: ogImageFile, type: 'ogImage', field: 'ogImageUrl' });

      for (const upload of uploads) {
        const url = await uploadFile(upload.file, upload.type);
        uploadedUrls[upload.field] = url;
      }

      // Prepare updated book data
      const updatedBook = {
        ...book,
        ...formData,
        ...uploadedUrls,
        updatedAt: new Date(),
        collection: `pb-books`
      };

      await firestoreService.updateBook(book.id, updatedBook);
      onUpdate(updatedBook);

      // Clear file states
      setLogoFile(null);
      setCoverImageFile(null);
      setFaviconFile(null);
      setOgImageFile(null);

      onClose();
    } catch (error) {
      console.error('Error saving book settings:', error);
      alert('Error saving settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const selectedCategory = BOOK_CATEGORIES.find(cat => cat.value === formData.botCategory);
  
  const previewStyles = {
    '--book-primary': formData.primaryColor,
    '--book-background': formData.backgroundColor,
    '--book-header': formData.headerColor,
    '--book-header-text': formData.headerTextColor,
    '--book-accent': formData.accentColor,
    '--book-border': formData.borderColor,
    fontFamily: formData.fontFamily,
    borderRadius: formData.borderRadius
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Book Settings</h2>
            <p className="text-sm text-gray-600 mt-1">
              Configure your book's appearance, behavior, and access settings
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar Navigation */}
          <div className="w-64 bg-gray-50 border-r overflow-y-auto">
            <nav className="p-4 space-y-1">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2 text-left rounded-lg transition-colors
                      ${activeTab === tab.id
                        ? 'bg-blue-100 text-blue-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-100'
                      }
                    `}
                  >
                    <IconComponent className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-6">
            
            {/* General Tab */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Book Title *
                      </label>
                      <input
                        type="text"
                        value={formData.botName}
                        onChange={(e) => handleInputChange('botName', e.target.value)}
                        placeholder="Enter your book title"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Book Category *
                      </label>
                      <select
                        value={formData.botCategory}
                        onChange={(e) => handleInputChange('botCategory', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        {BOOK_CATEGORIES.map(category => {
                          return (
                            <option key={category.value} value={category.value}>
                              {category.label}
                            </option>
                          );
                        })}
                      </select>
                      {selectedCategory && (
                        <p className="text-sm text-gray-500 mt-1">
                          {selectedCategory.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Describe what this book is about..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Category Preview */}
                {selectedCategory && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <selectedCategory.icon className="h-6 w-6 text-blue-600" />
                      <div>
                        <h4 className="font-medium text-blue-900">{selectedCategory.label}</h4>
                        <p className="text-sm text-blue-700">{selectedCategory.description}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Styling Tab */}
            {activeTab === 'styling' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Visual Styling</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Color
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.primaryColor}
                        onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                        className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.primaryColor}
                        onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Background Color
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.backgroundColor}
                        onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                        className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.backgroundColor}
                        onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Header Color
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.headerColor}
                        onChange={(e) => handleInputChange('headerColor', e.target.value)}
                        className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.headerColor}
                        onChange={(e) => handleInputChange('headerColor', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Accent Color
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.accentColor}
                        onChange={(e) => handleInputChange('accentColor', e.target.value)}
                        className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.accentColor}
                        onChange={(e) => handleInputChange('accentColor', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Font Family
                    </label>
                    <select
                      value={formData.fontFamily}
                      onChange={(e) => handleInputChange('fontFamily', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="Inter, system-ui, sans-serif">Inter (Modern)</option>
                      <option value="Helvetica, Arial, sans-serif">Helvetica (Classic)</option>
                      <option value="Georgia, serif">Georgia (Serif)</option>
                      <option value="'Courier New', monospace">Courier (Monospace)</option>
                      <option value="'SF Pro Display', system-ui">SF Pro (Apple)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Border Radius
                    </label>
                    <select
                      value={formData.borderRadius}
                      onChange={(e) => handleInputChange('borderRadius', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="0px">None (Square)</option>
                      <option value="4px">Small (4px)</option>
                      <option value="8px">Medium (8px)</option>
                      <option value="12px">Large (12px)</option>
                      <option value="24px">Extra Large (24px)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Media Assets Tab */}
            {activeTab === 'assets' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Media Assets</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Logo Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Logo
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      {logoPreview ? (
                        <div className="space-y-2">
                          <img 
                            src={logoPreview} 
                            alt="Logo preview" 
                            className="h-16 w-auto mx-auto object-contain"
                          />
                          <button
                            onClick={() => {
                              setLogoPreview('');
                              setLogoFile(null);
                              handleInputChange('logoUrl', '');
                            }}
                            className="text-sm text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div>
                          <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-600">Upload logo image</p>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileSelect('logo', e)}
                        className="hidden"
                        id="logo-upload"
                      />
                      <label
                        htmlFor="logo-upload"
                        className="inline-block mt-2 px-3 py-1 bg-blue-600 text-white text-sm rounded cursor-pointer hover:bg-blue-700"
                      >
                        {uploading.logo ? 'Uploading...' : 'Choose File'}
                      </label>
                    </div>
                  </div>

                  {/* Cover Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cover Image
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      {coverImagePreview ? (
                        <div className="space-y-2">
                          <img 
                            src={coverImagePreview} 
                            alt="Cover preview" 
                            className="h-16 w-auto mx-auto object-contain"
                          />
                          <button
                            onClick={() => {
                              setCoverImagePreview('');
                              setCoverImageFile(null);
                              handleInputChange('coverImageUrl', '');
                            }}
                            className="text-sm text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div>
                          <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-600">Upload cover image</p>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileSelect('cover', e)}
                        className="hidden"
                        id="cover-upload"
                      />
                      <label
                        htmlFor="cover-upload"
                        className="inline-block mt-2 px-3 py-1 bg-blue-600 text-white text-sm rounded cursor-pointer hover:bg-blue-700"
                      >
                        {uploading.cover ? 'Uploading...' : 'Choose File'}
                      </label>
                    </div>
                  </div>

                  {/* Favicon Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Favicon
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      {faviconPreview ? (
                        <div className="space-y-2">
                          <img 
                            src={faviconPreview} 
                            alt="Favicon preview" 
                            className="h-8 w-8 mx-auto object-contain"
                          />
                          <button
                            onClick={() => {
                              setFaviconPreview('');
                              setFaviconFile(null);
                              handleInputChange('faviconUrl', '');
                            }}
                            className="text-sm text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div>
                          <Globe className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-600">Upload favicon (16x16 or 32x32)</p>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileSelect('favicon', e)}
                        className="hidden"
                        id="favicon-upload"
                      />
                      <label
                        htmlFor="favicon-upload"
                        className="inline-block mt-2 px-3 py-1 bg-blue-600 text-white text-sm rounded cursor-pointer hover:bg-blue-700"
                      >
                        {uploading.favicon ? 'Uploading...' : 'Choose File'}
                      </label>
                    </div>
                  </div>

                  {/* OG Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Social Preview (OG Image)
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      {ogImagePreview ? (
                        <div className="space-y-2">
                          <img 
                            src={ogImagePreview} 
                            alt="OG Image preview" 
                            className="h-16 w-auto mx-auto object-contain"
                          />
                          <button
                            onClick={() => {
                              setOgImagePreview('');
                              setOgImageFile(null);
                              handleInputChange('ogImageUrl', '');
                            }}
                            className="text-sm text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div>
                          <Eye className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-600">Upload social media preview (1200x630)</p>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileSelect('ogImage', e)}
                        className="hidden"
                        id="og-upload"
                      />
                      <label
                        htmlFor="og-upload"
                        className="inline-block mt-2 px-3 py-1 bg-blue-600 text-white text-sm rounded cursor-pointer hover:bg-blue-700"
                      >
                        {uploading.ogImage ? 'Uploading...' : 'Choose File'}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Access & Privacy Tab */}
            {activeTab === 'access' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Access & Privacy</h3>
                
                {/* Access Level */}
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Shield className="h-5 w-5 text-blue-600" />
                    Access Level
                  </h4>
                  <select
                    value={formData.accessLevel}
                    onChange={(e) => handleInputChange('accessLevel', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="private">Private - Only you can access</option>
                    <option value="shared">Shared - Specific users can access</option>
                    <option value="public">Public - Anyone can access</option>
                  </select>
                  
                  <div className="mt-4 flex items-center gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.published}
                        onChange={(e) => handleInputChange('published', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Published</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.allowAnonymous}
                        onChange={(e) => handleInputChange('allowAnonymous', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Allow Anonymous Access</span>
                    </label>
                  </div>
                </div>

                {/* Authorized Users */}
                {formData.accessLevel === 'shared' && (
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <Users className="h-5 w-5 text-green-600" />
                      Authorized Users
                    </h4>
                    
                    <div className="flex gap-2 mb-4">
                      <input
                        type="email"
                        value={newUserEmail}
                        onChange={(e) => setNewUserEmail(e.target.value)}
                        placeholder="Enter email address"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        onClick={addAuthorizedUser}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Add
                      </button>
                    </div>

                    {emailError && (
                      <p className="text-red-600 text-sm mb-4">{emailError}</p>
                    )}

                    <div className="space-y-2">
                      {formData.authorizedUsers.map((email, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-700">{email}</span>
                          <button
                            onClick={() => removeAuthorizedUser(email)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      {formData.authorizedUsers.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">
                          No authorized users added yet
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Response Settings Tab */}
            {activeTab === 'responses' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Response Settings</h3>
                
                {/* Time Limits */}
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Timer className="h-5 w-5 text-orange-600" />
                    Time Limits
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Time Limit (minutes)
                      </label>
                      <input
                        type="number"
                        value={formData.responseSettings.timeLimit || ''}
                        onChange={(e) => handleInputChange('responseSettings.timeLimit', e.target.value ? parseInt(e.target.value) : null)}
                        placeholder="No time limit"
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Attempts
                      </label>
                      <input
                        type="number"
                        value={formData.responseSettings.maxAttempts || ''}
                        onChange={(e) => handleInputChange('responseSettings.maxAttempts', e.target.value ? parseInt(e.target.value) : null)}
                        placeholder="Unlimited"
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    {[
                      { key: 'showTimer', label: 'Show Timer', desc: 'Display remaining time to users' },
                      { key: 'allowResumeIncomplete', label: 'Allow Resume Incomplete', desc: 'Users can save and continue later' },
                      { key: 'allowEditAfterSubmission', label: 'Allow Edit After Submission', desc: 'Users can modify submitted responses' },
                      { key: 'enableProgressTracking', label: 'Enable Progress Tracking', desc: 'Track completion progress' },
                      { key: 'allowDownloadResponses', label: 'Allow Download Responses', desc: 'Users can download their responses' }
                    ].map(({ key, label, desc }) => (
                      <label key={key} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <input
                          type="checkbox"
                          checked={formData.responseSettings[key]}
                          onChange={(e) => handleInputChange(`responseSettings.${key}`, e.target.checked)}
                          className="mt-1 text-blue-600 focus:ring-blue-500"
                        />
                        <div>
                          <div className="font-medium text-sm">{label}</div>
                          <div className="text-xs text-gray-600">{desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Auto-save Settings */}
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Database className="h-5 w-5 text-green-600" />
                    Auto-save Settings
                  </h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Auto-save Interval (seconds)
                    </label>
                    <select
                      value={formData.responseSettings.autoSaveInterval}
                      onChange={(e) => handleInputChange('responseSettings.autoSaveInterval', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={15}>Every 15 seconds</option>
                      <option value={30}>Every 30 seconds</option>
                      <option value={60}>Every minute</option>
                      <option value={300}>Every 5 minutes</option>
                    </select>
                  </div>
                </div>

                {/* Submission Settings */}
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Check className="h-5 w-5 text-purple-600" />
                    Submission Settings
                  </h4>
                  
                  <div className="space-y-4">
                    <label className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.responseSettings.requireSubmission}
                        onChange={(e) => handleInputChange('responseSettings.requireSubmission', e.target.checked)}
                        className="mt-1 text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <div className="font-medium text-sm">Require Submission</div>
                        <div className="text-xs text-gray-600">Users must explicitly submit their responses</div>
                      </div>
                    </label>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Custom Submission Message
                      </label>
                      <textarea
                        value={formData.responseSettings.customSubmissionMessage}
                        onChange={(e) => handleInputChange('responseSettings.customSubmissionMessage', e.target.value)}
                        placeholder="Enter custom message shown after submission"
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      />
                      <p className="text-sm text-gray-500 mt-2">
                        Leave blank to show the default submission message
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Notifications</h3>
                
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Mail className="h-5 w-5 text-blue-600" />
                    Email Notifications
                  </h4>
                  
                  <div className="space-y-4">
                    <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.responseSettings.notifyOnSubmission}
                        onChange={(e) => handleInputChange('responseSettings.notifyOnSubmission', e.target.checked)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <div className="font-medium">Notify on Response Submission</div>
                        <div className="text-sm text-gray-600">Get email notifications when users submit responses</div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Domain & SEO Tab */}
            {activeTab === 'domain' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Domain & SEO</h3>
                
                {/* Domain Configuration */}
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Globe className="h-5 w-5 text-blue-600" />
                    Domain Configuration
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Custom Domain
                      </label>
                      <input
                        type="text"
                        value={formData.customDomain}
                        onChange={(e) => handleInputChange('customDomain', e.target.value)}
                        placeholder="mybook.example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Configure a custom domain for your book
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Custom Path
                      </label>
                      <input
                        type="text"
                        value={formData.customPath}
                        onChange={(e) => handleInputChange('customPath', e.target.value)}
                        placeholder="/my-custom-book"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Custom path for your book URL
                      </p>
                    </div>
                  </div>

                  {/* URL Preview */}
                  {(formData.customDomain || formData.customPath) && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <h5 className="font-medium text-blue-900 mb-2">URL Preview</h5>
                      <p className="text-sm text-blue-700 font-mono">
                        {formData.customDomain 
                          ? `https://${formData.customDomain}${formData.customPath || ''}`
                          : `https://parrotpages.com${formData.customPath || `/books/${book?.id}`}`
                        }
                      </p>
                    </div>
                  )}
                </div>

                {/* SEO Configuration */}
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Eye className="h-5 w-5 text-green-600" />
                    SEO Configuration
                  </h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SEO Title
                      </label>
                      <input
                        type="text"
                        value={formData.seoTitle}
                        onChange={(e) => handleInputChange('seoTitle', e.target.value)}
                        placeholder="Title for search engines"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        {formData.seoTitle?.length || 0}/60 characters
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SEO Description
                      </label>
                      <textarea
                        value={formData.seoDescription}
                        onChange={(e) => handleInputChange('seoDescription', e.target.value)}
                        placeholder="Description for search engines and social media"
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        {formData.seoDescription?.length || 0}/160 characters
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SEO Keywords
                      </label>
                      <input
                        type="text"
                        value={formData.seoKeywords}
                        onChange={(e) => handleInputChange('seoKeywords', e.target.value)}
                        placeholder="keyword1, keyword2, keyword3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Separate keywords with commas
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Custom CSS Tab */}
            {activeTab === 'css' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Custom CSS</h3>
                  <div className="text-sm text-gray-500">
                    Use CSS variables: --book-primary, --book-background, --book-header
                  </div>
                </div>

                <textarea
                  value={formData.customCSS}
                  onChange={(e) => handleInputChange('customCSS', e.target.value)}
                  placeholder="/* Add your custom CSS here */
.custom-header {
  background: var(--book-primary);
  color: white;
  padding: 1rem;
  border-radius: var(--book-border-radius);
}"
                  rows={15}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                />
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-yellow-800">CSS Guidelines</h4>
                      <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                        <li>• Use CSS variables for consistent theming</li>
                        <li>• Test your CSS across different devices</li>
                        <li>• Avoid overriding critical system styles</li>
                        <li>• Custom CSS applies to the entire book</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Preview Tab */}
            {activeTab === 'preview' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Preview</h3>
                  <div className="flex gap-2">
                    {Object.entries(DEVICE_BREAKPOINTS).map(([key, device]) => {
                      const icons = {
                        mobile: Smartphone,
                        tablet: Tablet,
                        desktop: Monitor,
                        wide: Monitor
                      };
                      const Icon = icons[key];
                      
                      return (
                        <button
                          key={key}
                          onClick={() => setPreviewDevice(key)}
                          className={`
                            flex items-center gap-1 px-3 py-2 text-sm rounded-lg transition-colors
                            ${previewDevice === key
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-600 hover:bg-gray-100'
                            }
                          `}
                        >
                          <Icon className="h-4 w-4" />
                          {device.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Preview Container */}
                <div className="bg-gray-100 p-8 rounded-lg flex justify-center">
                  <div 
                    className="bg-white border rounded-lg shadow-lg overflow-hidden transition-all duration-300"
                    style={{
                      width: DEVICE_BREAKPOINTS[previewDevice].width,
                      maxWidth: '100%',
                      ...previewStyles
                    }}
                  >
                    {/* Preview Header */}
                    <div 
                      className="p-4 text-white"
                      style={{ backgroundColor: formData.headerColor }}
                    >
                      <div className="flex items-center gap-3">
                        {logoPreview && (
                          <img 
                            src={logoPreview} 
                            alt="Logo" 
                            className="h-8 w-auto object-contain"
                          />
                        )}
                        <div>
                          <h4 className="font-semibold" style={{ color: formData.headerTextColor }}>
                            {formData.botName || 'Book Title'}
                          </h4>
                          {selectedCategory && (
                            <div className="flex items-center gap-2 mt-1">
                              <selectedCategory.icon 
                                className="h-4 w-4" 
                                style={{ color: formData.headerTextColor }} 
                              />
                              <span 
                                className="text-sm" 
                                style={{ color: formData.headerTextColor }}
                              >
                                {selectedCategory.label}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Preview Content */}
                    <div className="p-4" style={{ backgroundColor: formData.backgroundColor }}>
                      <div 
                        className="p-3 rounded mb-3"
                        style={{ 
                          backgroundColor: formData.primaryColor + '10',
                          border: `1px solid ${formData.primaryColor}30`,
                          borderRadius: formData.borderRadius
                        }}
                      >
                        <p className="text-sm" style={{ color: formData.primaryColor }}>
                          Sample content with primary color theming
                        </p>
                      </div>

                      <div 
                        className="p-3 rounded"
                        style={{ 
                          backgroundColor: formData.accentColor + '10',
                          border: `1px solid ${formData.accentColor}30`,
                          borderRadius: formData.borderRadius
                        }}
                      >
                        <p className="text-sm" style={{ color: formData.accentColor }}>
                          Accent color element example
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !formData.botName}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};